import React from 'react';
import { Button, Dialog, Dropdown, Label, Text } from 'components';
import { Callout } from '@blueprintjs/core';
import { LINES_QUERY, TRANSFER_USER_TO_FUNCTION_MUTATION } from 'gql';
import { useQuery, useMutation } from '@apollo/client';
import { useFormik } from 'formik';
import { TRANSFER_USER_TO_FUNCTION_VALIDATION } from 'validations';
import { useModal } from 'providers/modalProvider';
import { useToast } from 'providers/toastProvider';
import { useIntl, FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { prop, propEq, reject, toLower } from 'ramda';
import { getFunctions, optionsFromArr, title } from 'utils/helpers';
import { SPORTS } from 'utils/enums';

const TransferUserToFunction = () => {
  const { formatMessage } = useIntl();
  const { modalDispatch, modalState } = useModal();
  const { showToast } = useToast();
  const { user = {}, refetch } = modalState;
  const { id: userId, firstName, middleName, lastName } = user;
  // A function move stays on the user's own line, so the target list is scoped
  // to that line — no sport / line pickers like the cross-line transfer has.
  const sourceLineId = user?.jobTitleRole?.role?.line?.id;
  const sourceSport = user?.jobTitleRole?.role?.line?.sport;
  const currentLineFunctionId = user?.lineFunction?.id;
  // Current context shown read-only so the admin sees where the user is before
  // picking the new function.
  const sportLabel = sourceSport ? prop(toLower(sourceSport), SPORTS) : '';
  const sourceLineName = user?.jobTitleRole?.role?.line?.name || '';
  const currentFunctionName = user?.lineFunction?.name || '';
  const fullName = `${firstName || ''} ${lastName || ''}`.trim();
  const headerName = `${firstName || ''} ${middleName || ''} ${lastName || ''}`
    .replace(/\s+/g, ' ')
    .trim();

  const closeModal = () => modalDispatch({ type: 'CLOSE_ALL' });

  const { data: { lines = [] } = {}, loading: linesLoading } =
    useQuery(LINES_QUERY);

  const [transferMutation, { loading: transferLoading }] = useMutation(
    TRANSFER_USER_TO_FUNCTION_MUTATION,
    {
      // Keep the dialog open on error so the admin can adjust and retry; the
      // toast carries the server's (already user-facing) message.
      onError: (err) => {
        const message = (
          err.graphQLErrors?.[0]?.message || err.message
        ).replace(/^GraphQL error:\s*/, '');
        showToast({ message, icon: 'error' });
      },
      onCompleted: () => {
        closeModal();
        showToast({
          message: formatMessage({
            id: 'containers.users.modals.changeFunction.success',
          }),
        });
        refetch?.();
      },
    }
  );

  const formik = useFormik({
    initialValues: { lineFunctionId: '' },
    validationSchema: TRANSFER_USER_TO_FUNCTION_VALIDATION,
    onSubmit: (values) =>
      transferMutation({
        variables: {
          input: { userId, targetLineFunctionId: values.lineFunctionId },
        },
      }),
  });

  // Target functions are the user's own line's functions, minus the one they
  // are already in (moving to the same function would be a no-op).
  const lineFunctionOptions = optionsFromArr(
    reject(
      propEq('id', currentLineFunctionId),
      getFunctions(lines, sourceSport, sourceLineId)
    )
  );
  // Nothing to move to (line has only the current function, or the user has no
  // line). Surface it instead of letting the admin submit an empty selection.
  const hasNoFunctions = !linesLoading && lineFunctionOptions.length === 0;

  // Read-only current-context item, rendered like MigrateUserDialog's details.
  const detail = (label, value, testid) => (
    <Label
      variant="Baisc"
      labelHeader={label}
      labelText={value || '—'}
      data-testid={`change-function-${testid}`}
    />
  );

  return (
    <Dialog
      dialogTitle={
        <FormattedMessage id="containers.users.modals.changeFunction.title" />
      }
      className="transfer-dialog"
      width="540px"
      icon="follower"
    >
      <div className="bp3-dialog-body" data-testid="change-function-body">
        <div data-testid="change-function-form">
          <Callout
            className="transfer-help"
            intent="primary"
            icon="info-sign"
            data-testid="change-function-help"
          >
            <FormattedMessage id="containers.users.modals.changeFunction.help" />
          </Callout>
          <div className={classNames('flex', 'gap-15')}>
            <Label
              variant="AvatarLabel"
              labelText={headerName}
              avatarText={fullName}
              className="mgb-15"
            />
            <Label
              variant="BorderLabel"
              className="mgb-15 change-function-context"
              labelsList={[
                detail(
                  formatMessage({ id: 'containers.users.header.sport' }),
                  sportLabel,
                  'sport'
                ),
                detail(
                  formatMessage({ id: 'containers.users.form.line' }),
                  title(sourceLineName),
                  'line'
                ),
                detail(
                  formatMessage({ id: 'containers.users.form.lineFunction' }),
                  title(currentFunctionName),
                  'function'
                ),
              ]}
            />
          </div>

          {hasNoFunctions ? (
            <Text variant="body1" data-testid="change-function-empty">
              <FormattedMessage id="containers.users.modals.changeFunction.noFunctions" />
            </Text>
          ) : (
            <div className="transfer-row">
              <Dropdown
                items={lineFunctionOptions}
                value={formik.values.lineFunctionId || ''}
                onChange={({ value }) =>
                  formik.setFieldValue('lineFunctionId', value)
                }
                error={
                  formik.touched.lineFunctionId && formik.errors.lineFunctionId
                }
                buttonText={formatMessage({
                  id: 'containers.users.form.lineFunction',
                })}
                name="change-function-lineFunction"
                data-testid="change-function-lineFunction"
                loading={linesLoading}
                disableDropdownButton={linesLoading}
              />
            </div>
          )}
        </div>
      </div>
      <div className="bp3-dialog-footer" data-testid="change-function-footer">
        <div className="bp3-dialog-footer-actions">
          <Button
            outlined
            intent="Primary"
            text={formatMessage({ id: 'containers.all.btn.cancel' })}
            onClick={closeModal}
            data-testid="change-function-cancel"
          />
          <Button
            intent="Primary"
            text={formatMessage({
              id: 'containers.users.modals.changeFunction.submit',
            })}
            onClick={formik.submitForm}
            loading={transferLoading}
            disabled={hasNoFunctions}
            data-testid="change-function-submit"
          />
        </div>
      </div>
    </Dialog>
  );
};

export default TransferUserToFunction;
